import React from 'react';
import {
  Box,
  TextInput,
  Button,
  ArrowLeftIcon,
  ActionList,
  DropdownOverlay,
  Dropdown,
  ActionListItem,
  SelectInput,
  useToast,
} from '@razorpay/blade/components';
import { useForm } from 'forms';
import { useHistory } from 'react-router-dom';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import { routePaths } from 'components/Routes/data';
import { CompensationFormFields } from './types';
import {
  compensationValidationSchema,
  getAmountPayload,
  getCompensationStepPayload,
  invalidateDraftEmployeeCache,
  updateInitialCompensationFormData,
} from './utils';
import { initialCompensationData } from './data';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { CustomSalaryStructureForm } from './components/salaryStructure/CustomSalaryStructureForm';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError } from 'utils/AppError';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { useEmployeeCustomSalaryStructure } from './components/salaryStructure/hooks/useEmployeeCustomSalaryStructure';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

export const CompensationDetails = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}: {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}) => {
  const { draftEmployeeId } = useDraftEmployeeId();
  const { register, formData, validate, setIsSubmitting, isSubmitting, setFormData } =
    useForm<CompensationFormFields>(initialCompensationData);
  const history = useHistory();
  const { isMileStone1Enabled: isRearchEnabled } = useIsFeatureDisabledForRearch();
  const isRearchDisabled = !isRearchEnabled;
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleStepBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.basicDetails,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const { isLoading, isFetching, data } = useDraftEmployeeData({
    onSuccess: (data) => updateInitialCompensationFormData(data, setFormData),
  });

  const employeeCustomSalaryStructureHandlers = useEmployeeCustomSalaryStructure({
    compensationDetails: data?.compensationDetails,
  });

  const { mutateAsync: updateDraftEmployeeCompensationStep } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.updateDraftEmployeeCompensationStep,
    onSuccess: () => {
      setIsSubmitting(false);
      invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
    },
    onError: (error: typeof AppError) => {
      setIsSubmitting(false);
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();

    const isValidForm = validate(compensationValidationSchema);
    if (!isValidForm) {
      return;
    }
    setIsSubmitting(true);

    let salaryStructureType = formData.salaryStructureType?.[0] ?? 'default';
    if (isRearchEnabled) {
      salaryStructureType = 'default';
    }

    const res = await updateDraftEmployeeCompensationStep({
      draftEmployeeId,
      input: {
        salaryStructure: salaryStructureType,
        ctc:
          formData.salaryStructureType?.[0] === 'default'
            ? getAmountPayload(formData.annualCtc)
            : undefined,
        variablePay: isRearchDisabled ? getAmountPayload(formData.variablePay) : undefined,
        component:
          salaryStructureType === 'custom'
            ? getCompensationStepPayload(employeeCustomSalaryStructureHandlers.components)
            : undefined,
      },
    });

    if (res.payrollUpdateDraftEmployeeCompensation.data.id) {
      onSuccess();
    }
  };

  const handleContinueSuccess = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compliance,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const handleSaveAndCloseSuccess = () => {
    setShowSaveDraftAndCloseModal(true);
  };

  const shouldDisableButtons = isLoading || isFetching || isSubmitting;

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={(e) => handleSubmit(e, handleContinueSuccess)}>
        <FormFieldsWrapper shouldShowSpinner={isLoading || isFetching}>
          <FormHeading
            title="Compensation Details"
            description="Calculate the employee's gross pay for payroll processing"
          />
          <BorderedBox>
            {isRearchDisabled && (
              <Dropdown selectionType="single">
                <SelectInput
                  label="Salary Structure"
                  labelPosition="top"
                  {...register('salaryStructureType', 'select')}
                />
                <DropdownOverlay>
                  <ActionList>
                    <ActionListItem title="Default Salary Structure" value="default" />
                    <ActionListItem title="Custom Salary Structure" value="custom" />
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            )}

            {formData.salaryStructureType?.[0] === 'default' && (
              <TextInput
                {...register('annualCtc')}
                label="Annual CTC"
                autoFocus
                placeholder="0"
                size="medium"
                necessityIndicator="required"
                isRequired
                type="number"
                prefix="₹"
                helpText="Annual salary is the employee's CTC (excluding variable pay). If you have monthly CTC, multiply by 12. XPayroll will generate employee compensation based on salary structure selected."
              />
            )}

            {isRearchDisabled && (
              <TextInput
                {...register('variablePay')}
                label="Variable Pay"
                placeholder="0"
                size="medium"
                type="number"
                prefix="₹"
                helpText="Annual bonus, incentive etc. that is not included in the monthly salary by default. This is in addition to the Annual CTC"
              />
            )}
          </BorderedBox>

          {formData.salaryStructureType?.[0] === 'custom' && (
            <CustomSalaryStructureForm
              isFormBeingSubmitted={shouldDisableButtons}
              employeeCustomSalaryStructureHandlers={employeeCustomSalaryStructureHandlers}
            />
          )}
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={handleStepBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              size="medium"
              onClick={(e) => handleSubmit(e, handleSaveAndCloseSuccess)}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button type="submit" variant="primary" size="medium" isLoading={shouldDisableButtons}>
              Continue
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>
    </>
  );
};
